import { skillForSections, type SkillIconName } from '@/components/ui/SkillIcon'
import { authoredObjectivePointCapacity } from '@/domain/scoring/objective-points'
import type { SectionType, TestDefinition } from '@/lib/types'
import { examTypeLabel, ieltsModuleLabel } from '@/domain/exam-classification'
import { formatHoursMinutes, formatList, localizeDigits, t, tIn } from '@/lib/i18n'

/**
 * One presenter derives every display fact an exam-library card shows
 * (ticket #285): canonically identical tests must render identically
 * regardless of authoring vintage, and malformed metadata must degrade to a
 * usable row with a working CTA — never a crash or a hidden exam.
 *
 * Rules that live here on purpose:
 * - Display facts come from canonical `section.type` (falling back to the
 *   authored `types` list only when the row carries no sections), never from
 *   display titles.
 * - Academic/General and exam-purpose labels come only from the explicit
 *   IELTS-265/266 fields. Legacy rows are named as unclassified; their
 *   structural skills remain a separate fact and are never promoted into a
 *   guessed exam purpose.
 * - Entitlement (PRODUCT-CONTRACT §5.2) disables a row with honest copy; it
 *   never hides the row, so server pagination totals stay truthful.
 */

export type TestAvailability = 'startable' | 'locked' | 'resume' | 'history' | 'unavailable'

export interface TestCardStructure {
  /** Explicit exam purpose, or `null` when unclassified so callers omit it. */
  category: string | null
  /** Explicit authored module, or `null` when unclassified so callers omit it. */
  ieltsModule: string | null
  /** Canonical authored skills, kept separate from exam purpose. */
  skillsLabel: string
  monogram: string
  /**
   * Which bespoke skill mark represents this row, derived from the same
   * canonical section types as `skillsLabel` — never from the display title.
   * `null` when the row is neither a full mock nor a single-skill paper, so a
   * mixed two-paper test can never claim a skill it does not have.
   */
  skill: SkillIconName | null
  levelLabel: string
  durationLabel: string
  moduleCount: number
  /** Authored Listening/Reading capacity; null when the objective definition is incomplete. */
  objectivePointCount: number | null
  availability: TestAvailability
  ctaLabel: string
  /** Raw facts behind the English labels, for render-time interface copy. */
  types: SectionType[]
  durationMinutes: number | null
}

export interface TestCardPresentation {
  id: string
  title: string
  description: string
  attemptsLabel: string
  /** Route for the start CTA; present even when the list row has no sections. */
  startPath: string
  structure: TestCardStructure
  /** Which of title/description are our own fallback copy rather than authored content. */
  titleIsFallback: boolean
  descriptionIsFallback: boolean
  attempts: number
}

const CANONICAL_TYPES: ReadonlyArray<SectionType> = ['Listening', 'Reading', 'Writing', 'Speaking']

function normalizeType(value: unknown): SectionType | null {
  if (typeof value !== 'string') return null
  const match = CANONICAL_TYPES.find((type) => type.toLowerCase() === value.trim().toLowerCase())
  return match ?? null
}

/** Canonical section types, deduplicated, from sections first and `types` as fallback. */
export function canonicalSectionTypes(test: TestDefinition): SectionType[] {
  const fromSections = (Array.isArray(test.sections) ? test.sections : [])
    .map((section) => normalizeType(section?.type))
    .filter((type): type is SectionType => type !== null)
  const source = fromSections.length
    ? fromSections
    : (Array.isArray(test.types) ? test.types : []).map(normalizeType).filter((type): type is SectionType => type !== null)
  return CANONICAL_TYPES.filter((type) => source.includes(type))
}

/**
 * Ticket #355: the catalogue row no longer carries the Section→Part→Group→
 * Question tree — it was 1.7 MB and 67 extra queries per page of 12 cards to
 * produce this one number — so the server annotates the capacity instead
 * (`mocktest/services/objective_points.py` reproduces the rules in
 * `authoredObjectivePointCapacity` one for one, `null` included).
 *
 * `undefined` means the payload predates the annotation — a detail row or a
 * demo fixture, both of which still carry the nested tree — so that path keeps
 * computing locally and the card renders identically either way.
 */
export function sumAuthoredObjectivePoints(test: TestDefinition): number | null {
  if (test.objective_point_count !== undefined) {
    const annotated = test.objective_point_count
    return typeof annotated === 'number' && Number.isFinite(annotated) ? annotated : null
  }
  return authoredObjectivePointCapacity(test.sections)
}

export function testAvailability(test: TestDefinition, _now: Date = new Date()): TestAvailability {
  if (test.can_start_new_attempt === false) {
    if (test.active_run_id) return 'resume'
    if (test.latest_result_id) return 'history'
    return 'unavailable'
  }
  const subscriptions = Array.isArray(test.assigned_subscriptions) ? test.assigned_subscriptions : []
  if (subscriptions.length > 0 && !subscriptions.includes('Free')) return 'locked'
  return 'startable'
}

function monogramLabel(types: SectionType[]): string {
  if (types.length === 4) return 'FULL'
  if (types.length === 1) return types[0]!.slice(0, 1)
  return 'TEST'
}


const CTA_LABELS: Record<TestAvailability, string> = {
  startable: 'Open this test',
  locked: 'Not included in your plan',
  resume: 'Resume test',
  history: 'View results',
  unavailable: 'Currently unavailable',
}

export function presentTestCard(test: TestDefinition, now: Date = new Date()): TestCardPresentation {
  const types = canonicalSectionTypes(test)
  const availability = testAvailability(test, now)
  const minutes = typeof test.time_limit === 'number' && Number.isFinite(test.time_limit) ? test.time_limit : null

  const authoredTitle = test.display_title?.trim() || test.title?.trim() || ''
  const authoredDescription = test.description?.trim() || ''

  return {
    id: String(test.id),
    title: authoredTitle || 'Untitled practice test',
    description: authoredDescription || 'Practice details will be available when you open this test.',
    titleIsFallback: !authoredTitle,
    descriptionIsFallback: !authoredDescription,
    attempts: typeof test.tests_taken === 'number' ? test.tests_taken : 0,
    attemptsLabel: test.tests_taken ? String(test.tests_taken) : 'New',
    startPath: availability === 'history' ? `/results/${test.latest_result_id}` : `/test/${test.id}`,
    structure: {
      // Null when unclassified, so the card omits the row entirely rather than
      // telling a student "Exam type not classified". That phrasing is an
      // internal content-management concern leaking onto a student-facing card,
      // and it is on EVERY exam - none of the 83 carry classification yet
      // (IELTS-327). Staff surfaces keep the explicit label so the gap stays
      // visible to the people who can close it.
      category: test.exam_type ? examTypeLabel(test.exam_type) : null,
      ieltsModule: test.ielts_module ? ieltsModuleLabel(test.ielts_module) : null,
      skillsLabel: types.length ? types.join(', ') : 'Skills not listed',
      monogram: monogramLabel(types),
      skill: skillForSections(types),
      levelLabel: test.level || 'Practice',
      durationLabel: minutes === null ? 'Not set' : `${minutes} min`,
      moduleCount: types.length,
      objectivePointCount: sumAuthoredObjectivePoints(test),
      availability,
      ctaLabel: CTA_LABELS[availability],
      types,
      durationMinutes: minutes,
    },
  }
}

/** Every English label the presenter can emit, so the catalogue test can check them. */
export const TEST_CARD_INTERFACE_COPY: readonly string[] = [
  ...Object.values(CTA_LABELS), 'Untitled practice test', 'Practice details will be available when you open this test.',
  'New', 'Skills not listed', 'Not set', 'Practice', 'FULL', 'TEST', 'Beginner', 'Intermediate', 'Advanced',
]

/**
 * Render-time interface copy for one card, in the current language.
 * The presentation itself stays English source so it can be compared and
 * tested; authored titles and descriptions are never translated.
 */
export function testCardCopy(card: TestCardPresentation) {
  const { structure } = card
  return {
    title: card.titleIsFallback ? t(card.title) : card.title,
    description: card.descriptionIsFallback ? t(card.description) : card.description,
    attempts: card.attempts ? localizeDigits(String(card.attempts)) : t('New'),
    classification: [structure.category, structure.ieltsModule].filter((label): label is string => Boolean(label)).map((label) => t(label)),
    skills: structure.types.length ? formatList(structure.types.map((type) => t(type))) : t('Skills not listed'),
    // The monogram tile: FULL, TEST, or the skill's own letter (L, R, W, S).
    monogram: structure.types.length === 1 ? tIn('monogram', structure.monogram) : t(structure.monogram),
    level: t(structure.levelLabel),
    duration: structure.durationMinutes === null ? t('Not set') : formatHoursMinutes(structure.durationMinutes),
    cta: t(structure.ctaLabel),
  }
}
